<template lang="pug">
//- Without label text a <label> is empty, which screen-reader audits flag as an error
component.field(
  :is="label ? 'label' : 'div'"
  v-bind="rootAttrs"
  :class="{'field-required': required, 'field-error': errors && errors.length > 0}"
)
  span.block.label(v-if="label") {{ __(label) }}
    span.block.required(v-if="required") *
  slot
  span.block.field-error(v-if="errors.length > 0")
    | {{__(errors[0])}}
  span.block.field-messages(v-if="messages.length > 0")
    p.message(
      v-for="message in messages"
      :class="`${message.type}-message`"
    ) {{ message.text }}
</template>
<script setup lang="ts">
import { computed, useAttrs } from 'vue'

defineOptions({
  inheritAttrs: false,
})

type Message = { type: string, text: string }

type Props = {
  label?: string | null
  errors?: string[]
  messages?: Message[]
  required?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  label    : null,
  errors   : () => [],
  messages : () => [],
  required : false,
})

const attrs = useAttrs()

// `for` is only valid on a <label>
const rootAttrs = computed(() => {
  if (props.label) {
    return attrs
  }
  const { for: _for, ...rest } = attrs

  return rest
})
</script>
<style lang="scss">

  .field {
    display: block;
    position: relative;
    margin-bottom: var(--field-margin, #{$space-xs});
    user-select: none;

    .label {
      font-family: $font-sans-serif;
      font-size: var(--field-label-font-size, #{$size-xxs});
      display: block;
      margin-bottom: var(--field-label-margin, #{$space-xxxs});
    }

    .label-right {
      cursor: pointer;
    }

    .required {
      color: $color-red;
      display: inline-block;
      margin-left: $space-xxxs;
    }
  }

  .field-error {
    color: $color-red;
    font-size: $size-xxs;
  }

  .field-messages {
    font-size: $size-xxs;

    .warning-message {
      color: $color-red;
    }
  }
</style>

<style lang="scss">
  /* Dark mode styles - must be in non-scoped style block */
  [data-theme='dark'] .field,
  .dark-mode .field {
    .label {
      color: $color-gray-dark;
    }
  }
</style>
